import type { RealEstateSearchAddressResult } from "../../api/getRealEstateSearchAddress";
import type { PropertySelectionRow } from "../../types";
import {
  isRowOfPinProperty,
  type PinProperty,
} from "../propertySelection/pinProperty";

/**
 * 住居表示をピンを開いた時に取る検索結果かどうか。
 * 土地の検索は住居表示を空で返す（include_land_address: false）ので、空の土地だけが対象。
 * 建物と、駐車場の住所を持つ土地は検索時から住居表示が入っている
 */
export const needsLandAddress = (item: {
  bookType: string;
  address: string;
}): boolean => item.bookType === "LAND" && item.address === "";

type FetchAddress = (
  lat: number,
  lng: number
) => Promise<RealEstateSearchAddressResult>;

/**
 * 座標ごとに住居表示を1回だけ取るローダー。
 *
 * - 取れた結果（住居表示が無い空文字も含む）は覚えておき、同じ座標では API を呼ばない
 *   （ピンを開き直した時・検索し直して同じ土地が出た時）
 * - 取得中に同じ座標を頼まれたら、同じ取得の結果を待つ
 * - 失敗（429・503 など）は覚えない。次に頼まれた時にもう一度取る
 */
export const createLandAddressLoader = (
  fetchAddress: FetchAddress
): FetchAddress => {
  const found = new Map<string, string>();
  const loading = new Map<string, Promise<RealEstateSearchAddressResult>>();

  return async (lat, lng) => {
    const key = `${lat},${lng}`;
    const cached = found.get(key);
    if (cached !== undefined) return { kind: "found", address: cached };

    const inFlight = loading.get(key);
    if (inFlight) return await inFlight;

    const request = fetchAddress(lat, lng)
      .then((result) => {
        if (result.kind === "found") found.set(key, result.address);
        return result;
      })
      .finally(() => {
        loading.delete(key);
      });
    loading.set(key, request);
    return await request;
  };
};

/**
 * ピンを開いて取れた住居表示を、検索結果・物件リストの行に入れる。
 * その土地（種別・都道府県・所在・地番が同じ）で住居表示がまだ空のものだけを書き換え、ほかはそのまま返す。
 * 一覧に入れた行は検索結果を写した形（rawData.address、一括追加なら address も）なので両方を埋める。
 * 住居表示が無かった（空文字）時も、取ったことは残す（landAddressPending を外す）
 */
export const fillLandAddress = <T extends PropertySelectionRow>(
  rows: T[],
  property: PinProperty,
  address: string
): T[] => {
  if (property.bookType !== "LAND") return rows;
  let changed = false;
  const next = rows.map((row): T => {
    if (!isRowOfPinProperty(row, property)) return row;
    const topLevelEmpty =
      "address" in row && (row as { address?: unknown }).address === "";
    const rawDataEmpty =
      row.rawData !== undefined && row.rawData.address === "";
    const fillTopLevel = address !== "" && topLevelEmpty;
    const fillRawData = address !== "" && rawDataEmpty;
    if (!fillTopLevel && !fillRawData && !row.landAddressPending) return row;
    changed = true;
    return {
      ...row,
      landAddressPending: undefined,
      ...(fillTopLevel ? { address } : {}),
      ...(fillRawData && row.rawData
        ? { rawData: { ...row.rawData, address } }
        : {}),
    };
  });
  return changed ? next : rows;
};

/**
 * 物件リストを保存する時に送る住居表示。
 * 不動産検索の土地で住居表示をまだ取っていない行は null（空のまま保存する。保存のために取りには行かない）。
 * 取ったが住居表示が無かった行は空文字のまま送る
 */
export const addressForSave = (row: PropertySelectionRow): string | null =>
  row.landAddressPending ? null : row.rawData?.address ?? null;
